// SPDX-License-Identifier: GPL-3.0-or-later
// One sound effect voice: a gain node plus the oscillators and noise sources that feed it. A patch
// in sfx-bank.ts calls `tone` and `noise` to build a voice. The voice knows when it ends, so the
// polyphony manager can drop it. No audio file is used. Every sound is synthesised.

/** An oscillator part of a voice. Times are seconds from the start of the voice. */
export interface ToneSpec {
  readonly type: OscillatorType;
  /** Start frequency in Hz. Above 0. */
  readonly from: number;
  /** End frequency in Hz. The pitch glides to it. Above 0. */
  readonly to?: number;
  /** Delay from the start of the voice. */
  readonly at?: number;
  readonly dur: number;
  /** Level of this part, 0 to 1. Default 1. */
  readonly gain?: number;
  /** Attack time in seconds. Default 0.004. */
  readonly attack?: number;
  /** A low pass filter that sweeps from one cut-off to the other. */
  readonly lowpass?: { readonly from: number; readonly to: number };
}

/** A filtered noise part of a voice. */
export interface NoiseSpec {
  readonly filter: BiquadFilterType;
  readonly from: number;
  readonly to?: number;
  readonly q?: number;
  readonly at?: number;
  readonly dur: number;
  readonly gain?: number;
  readonly attack?: number;
}

/** A voice that a patch builds. */
export interface Voice {
  /** The level of the whole voice, 0 to 1. The manager uses it to rank voices. */
  readonly peak: number;
  /** Audio time at which the voice starts. */
  readonly start: number;
  /** Audio time at which the last part ends. */
  readonly end: number;
  tone(spec: ToneSpec): void;
  noise(spec: NoiseSpec): void;
  /** Fade the voice out fast and stop its sources. */
  kill(now: number): void;
  /** Cut the voice from the graph. Call it after the voice ended. */
  dispose(): void;
}

/** What a voice needs from the page. */
export interface VoiceEnv {
  readonly ctx: BaseAudioContext;
  /** The node that the voice connects to (the sfx bus). */
  readonly dest: AudioNode;
  /** The shared noise buffer. */
  noiseBuffer(): AudioBuffer;
}

const FLOOR = 0.0001;
const STOP_TAIL = 0.03;
const KILL_FADE = 0.01;
const DEFAULT_ATTACK = 0.004;

/** An envelope: from 0 up to `peak`, then an exponential fall to silence. */
export function applyEnvelope(
  param: AudioParam,
  t0: number,
  attack: number,
  peak: number,
  dur: number,
): void {
  const rise = Math.min(attack, dur / 2);
  param.setValueAtTime(0, t0);
  param.linearRampToValueAtTime(peak, t0 + rise);
  param.exponentialRampToValueAtTime(FLOOR, t0 + dur);
}

function sweep(
  param: AudioParam,
  from: number,
  to: number | undefined,
  t0: number,
  dur: number,
): void {
  param.setValueAtTime(from, t0);
  if (to !== undefined && to !== from) param.exponentialRampToValueAtTime(to, t0 + dur);
}

/** Make a noise buffer of one second. The generator is a fixed xorshift: it needs no random seed. */
export function makeNoiseBuffer(ctx: BaseAudioContext, seed = 0x9e3779b9): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let x = seed | 0 || 1;
  for (let i = 0; i < length; i++) {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    data[i] = (x >>> 0) / 0x80000000 - 1;
  }
  return buffer;
}

/** Make a voice. `peak` is the level of the whole voice. */
export function createVoice(env: VoiceEnv, start: number, peak: number): Voice {
  const { ctx } = env;
  const out = ctx.createGain();
  out.gain.value = peak;
  out.connect(env.dest);
  const sources: AudioScheduledSourceNode[] = [];
  let end = start;

  const place = (node: AudioNode, source: AudioScheduledSourceNode, t0: number, dur: number) => {
    node.connect(out);
    source.start(t0);
    source.stop(t0 + dur + STOP_TAIL);
    sources.push(source);
    end = Math.max(end, t0 + dur + STOP_TAIL);
  };

  return {
    peak,
    start,
    get end() {
      return end;
    },
    tone(spec) {
      const t0 = start + (spec.at ?? 0);
      const osc = ctx.createOscillator();
      osc.type = spec.type;
      sweep(osc.frequency, spec.from, spec.to, t0, spec.dur);
      const env2 = ctx.createGain();
      applyEnvelope(env2.gain, t0, spec.attack ?? DEFAULT_ATTACK, spec.gain ?? 1, spec.dur);
      if (spec.lowpass) {
        const lp = ctx.createBiquadFilter();
        lp.type = "lowpass";
        sweep(lp.frequency, spec.lowpass.from, spec.lowpass.to, t0, spec.dur);
        osc.connect(lp);
        lp.connect(env2);
      } else {
        osc.connect(env2);
      }
      place(env2, osc, t0, spec.dur);
    },
    noise(spec) {
      const t0 = start + (spec.at ?? 0);
      const src = ctx.createBufferSource();
      src.buffer = env.noiseBuffer();
      src.loop = spec.dur > 1;
      const filter = ctx.createBiquadFilter();
      filter.type = spec.filter;
      filter.Q.setValueAtTime(spec.q ?? 1, t0);
      sweep(filter.frequency, spec.from, spec.to, t0, spec.dur);
      const env2 = ctx.createGain();
      applyEnvelope(env2.gain, t0, spec.attack ?? DEFAULT_ATTACK, spec.gain ?? 1, spec.dur);
      src.connect(filter);
      filter.connect(env2);
      place(env2, src, t0, spec.dur);
    },
    kill(now) {
      out.gain.cancelScheduledValues(now);
      out.gain.setValueAtTime(out.gain.value, now);
      out.gain.linearRampToValueAtTime(0, now + KILL_FADE);
      for (const source of sources) {
        try {
          source.stop(now + KILL_FADE + 0.01);
        } catch {
          // The source already stopped.
        }
      }
      end = Math.min(end, now + KILL_FADE + 0.01);
    },
    dispose() {
      out.disconnect();
    },
  };
}
